import { useEffect, useState } from "react";
import { Alert, Checkbox, Chip, DialogContent, FormControlLabel, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Radio, Stack, Tooltip, Typography } from "@mui/material";
import { PrintOutlined, Refresh } from "@mui/icons-material";
import { MoleCancelButton, MoleDialog, MoleDialogActions, MoleDialogHeader, MolePrimaryButton, useMoleThemeColor } from "./MoleDialogShell";
import { useTranslation } from "react-i18next";
import { PickRequest, PrinterChoice, registerPrinterPicker } from "../util/printerPicker";

/**
 * App-wide printer dialog. "print" mode: a print found no app or system
 * default and several printers. "settings" mode: opened from the status bar
 * to choose this computer's app printer.
 */
const PrinterPickerHost = () => {
	const { t } = useTranslation();
	const themeColor = useMoleThemeColor();
	const [pending, setPending] = useState<PickRequest | null>(null);
	const [selected, setSelected] = useState<string>("");
	const [remember, setRemember] = useState(true);
	const [reloading, setReloading] = useState(false);

	useEffect(
		() =>
			registerPrinterPicker((request) => {
				const initial = [request.current, request.systemDefault].find((name) => name && request.printers.includes(name));
				setSelected(initial ?? request.printers[0] ?? "");
				setRemember(true);
				setPending((previous) => {
					previous?.resolve({ printer: null, remember: false });
					return request;
				});
			}),
		[],
	);

	const settings = pending?.mode === "settings";
	const printers = pending?.printers ?? [];

	const finish = (choice: PrinterChoice) => {
		pending?.resolve(choice);
		setPending(null);
	};
	const cancel = () => finish({ printer: null, remember: false });
	// Settings mode always saves; print mode saves only when "remember" is ticked.
	const choose = (printer: string) => finish({ printer, remember: settings || remember });

	const reload = async () => {
		if (!pending?.reload) return;
		setReloading(true);
		try {
			const listed = await pending.reload();
			setPending((current) =>
				current && {
					...current,
					printers: listed?.printers ?? [],
					systemDefault: listed?.systemDefault ?? null,
					unavailable: !listed,
				},
			);
			if (listed && !listed.printers.includes(selected)) setSelected(listed.printers[0] ?? "");
		} finally {
			setReloading(false);
		}
	};

	return (
		<MoleDialog open={!!pending} onClose={cancel} maxWidth="xs" fullWidth themeColor={themeColor}>
			<MoleDialogHeader
				title={t(settings ? "common:printer.settings.title" : "common:printer.pick.title")}
				icon={<PrintOutlined />}
				onClose={cancel}
				themeColor={themeColor}
				extra={
					pending?.reload ? (
						<Tooltip title={t("common:printer.settings.refresh")}>
							<span>
								<IconButton size="small" onClick={reload} disabled={reloading}>
									<Refresh fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					) : undefined
				}
			/>
			<DialogContent sx={{ pt: 2 }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{t(settings ? "common:printer.settings.description" : "common:printer.pick.description")}
				</Typography>
				{pending?.unavailable && (
					<Alert severity="warning" sx={{ mb: 1 }}>
						{t("common:printer.settings.unavailable")}
					</Alert>
				)}
				{!pending?.unavailable && printers.length === 0 && (
					<Alert severity="info" sx={{ mb: 1 }}>
						{t("common:printer.settings.none")}
					</Alert>
				)}
				<List dense disablePadding>
					{printers.map((printer) => (
						<ListItemButton
							key={printer}
							selected={printer === selected}
							onClick={() => setSelected(printer)}
							onDoubleClick={() => choose(printer)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									choose(printer);
								}
							}}
						>
							<ListItemIcon sx={{ minWidth: 36 }}>
								<Radio edge="start" size="small" checked={printer === selected} tabIndex={-1} disableRipple />
							</ListItemIcon>
							<ListItemText primary={printer} />
							<Stack direction="row" spacing={0.5}>
								{printer === pending?.current && <Chip size="small" color="primary" label={t("common:printer.settings.appDefault")} />}
								{printer === pending?.systemDefault && <Chip size="small" variant="outlined" label={t("common:printer.settings.systemDefault")} />}
							</Stack>
						</ListItemButton>
					))}
				</List>
				{!settings && (
					<FormControlLabel
						sx={{ mt: 1 }}
						control={<Checkbox size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
						label={t("common:printer.pick.remember")}
					/>
				)}
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={cancel}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				{settings && pending?.current && (
					<MoleCancelButton onClick={() => finish({ printer: null, remember: true })}>
						{t("common:printer.settings.useSystem")}
					</MoleCancelButton>
				)}
				<MolePrimaryButton disabled={!selected || !printers.includes(selected)} onClick={() => choose(selected)} startIcon={<PrintOutlined />}>
					{t(settings ? "common:printer.settings.setDefault" : "common:printer.pick.print")}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default PrinterPickerHost;
